import { useState } from "react";
import { Breadcrumb } from "../shell/Shell.js";
import { EmptyState } from "../components.js";
import {
  useRoadmap,
  ROADMAP_HORIZONS,
  type RoadmapData,
  type RoadmapEntry,
  type RoadmapHorizon,
} from "../api/useRoadmap.js";
import { Swimlane } from "./RoadmapSwimlane.js";
import "./roadmap.css";

/**
 * Timeline / roadmap screen (E8.14, NX-2643).
 *
 * DELIBERATE NAV RE-ADD, NOT A REGRESSION OF NX-2645: NX-2645 removed four nav destinations
 * (Forecast/Capability/Reliability/Friction) because the locked IA (design/08 §1) placed their
 * content as Project-detail panels, not top-level screens. Timeline is different: Papa ruled
 * 2026-08-21 that the plan wins here — E8.14 is a genuinely wanted top-level destination that the
 * locked IA simply predates, and the design doc is being updated separately. See the NAV entry in
 * `shell/TopBar.tsx` for the same note at the point someone would actually collide with it.
 *
 * A REAL, REPORTED GAP (see `useRoadmap.ts`'s module comment for the full explanation): the plan
 * asks for "rest-of-year / next 4·3·2·1 quarters"; `/roadmap` supports exactly two horizons
 * (`next-quarter`, `rest-of-year`). This screen offers only the two that are real, plus an honest
 * note about the missing three rather than faking them.
 */
export function Roadmap(): React.ReactNode {
  const [horizon, setHorizon] = useState<RoadmapHorizon>("rest-of-year");
  const state = useRoadmap(horizon);
  return (
    <>
      <Breadcrumb items={[{ label: "Timeline" }]} />
      <div className="head">
        <h1>Timeline</h1>
      </div>
      <HorizonSelector value={horizon} onChange={setHorizon} />
      {state.status === "loading" && (
        <div className="meta">Loading the roadmap from the evidence store…</div>
      )}
      {state.status === "error" && (
        <EmptyState message={`Couldn't load the roadmap: ${state.error.message}`} />
      )}
      {state.status === "ready" && (
        <RoadmapBody
          data={state.envelope.data}
          nextCursor={state.envelope.meta.nextCursor ?? null}
        />
      )}
    </>
  );
}

const HORIZON_LABEL: Record<RoadmapHorizon, string> = {
  "next-quarter": "Next quarter",
  "rest-of-year": "Rest of year",
};

export interface HorizonSelectorProps {
  readonly value: RoadmapHorizon;
  readonly onChange: (horizon: RoadmapHorizon) => void;
}

/**
 * AC1 — reads/writes the horizon that actually reaches the API's `horizon` query param (via the
 * injected `useRoadmap` hook in the parent), not client-only state that never reaches the server's
 * filter. Single responsibility: this component only renders the two real choices and reports the
 * gap; it does not know how to fetch.
 */
export function HorizonSelector({ value, onChange }: HorizonSelectorProps): React.ReactNode {
  return (
    <div className="horizonsel">
      <div role="radiogroup" aria-label="Roadmap horizon">
        {ROADMAP_HORIZONS.map((h) => (
          <button
            key={h}
            type="button"
            role="radio"
            aria-checked={value === h}
            className={value === h ? "on" : ""}
            onClick={() => onChange(h)}
          >
            {HORIZON_LABEL[h]}
          </button>
        ))}
      </div>
      <p className="meta horizonnote">
        The plan also asks for "next 4 / 3 / 2 quarters" — the roadmap API supports only these two
        horizons today. The narrower windows are an API follow-up, not simulated here by filtering a
        fetched page (that would silently disagree with the server's own undated/truncated counts).
      </p>
    </div>
  );
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** A shared time axis across every lane, so confidence bars are visually comparable (AC2). Built
 *  only from dates the API actually returned (each entry's own p95/target), never from a
 *  reimplementation of the server's horizon math — this is purely a RENDERING scale, not a filter. */
function windowEndFor(entries: readonly RoadmapEntry[], start: number): number {
  const candidates: number[] = [];
  for (const entry of entries) {
    if (entry.forecast.kind === "value")
      candidates.push(new Date(entry.forecast.value.p95Date).getTime());
    if (entry.targetDate !== null) candidates.push(new Date(entry.targetDate).getTime());
  }
  const max = candidates.length > 0 ? Math.max(...candidates) : start + 90 * DAY_MS;
  return Math.max(max, start + DAY_MS);
}

const sortKey = (entry: RoadmapEntry): number =>
  entry.forecast.kind === "value"
    ? new Date(entry.forecast.value.p50Date).getTime()
    : entry.targetDate !== null
      ? new Date(entry.targetDate).getTime()
      : Number.POSITIVE_INFINITY;

export interface RoadmapBodyProps {
  readonly data: RoadmapData;
  readonly nextCursor: string | null;
}

export function RoadmapBody({ data, nextCursor }: RoadmapBodyProps): React.ReactNode {
  const { entries, undated, truncated } = data;
  if (entries.length === 0 && undated === 0) {
    return <EmptyState message="No projects land in this horizon." />;
  }
  const windowStart = Date.now();
  const windowEnd = windowEndFor(entries, windowStart);
  const sorted = [...entries].sort((a, b) => sortKey(a) - sortKey(b));

  return (
    <>
      {(undated > 0 || truncated || nextCursor !== null) && (
        <div className="meta roadmaphonesty" role="note">
          {/* AC4 / the honesty fields — never swallowed. A roadmap that looks complete but isn't
              is exactly the failure this product exists to prevent (roadmap.ts's own comment). */}
          {undated > 0 && (
            <span>
              {undated} project{undated === 1 ? "" : "s"} excluded from this horizon — no forecast
              or declared date to place.{" "}
            </span>
          )}
          {truncated && (
            <span>
              This page is a bounded scan (truncated) — not the whole tenant's portfolio.{" "}
            </span>
          )}
          {nextCursor !== null && (
            <span>
              More projects exist beyond this page (pagination beyond page 1 is a follow-up).
            </span>
          )}
        </div>
      )}
      {entries.length === 0 ? (
        <EmptyState message="No projects with a forecast or declared date land in this horizon." />
      ) : (
        <div className="swimlanes">
          {sorted.map((entry) => (
            <Swimlane
              key={entry.id}
              entry={entry}
              windowStart={windowStart}
              windowEnd={windowEnd}
            />
          ))}
        </div>
      )}
    </>
  );
}
